import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { selectClient } from '@client/model';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { clientsApi, clientKeys } from '@client/api/waterlily';
import { guidedPlanningApi, guidedPlanningSessionKeys } from '@/features/guided-financial-planning/api';

interface PersistedSelection {
  action: string;
  partnerFundingSourceId?: string;
  originalValue: number;
  customClientPercent?: number;
}

/**
 * Fetches the partner client and their active guided planning session, then
 * returns the set of funding source IDs that the partner shared TO the current
 * client. These should be rendered as read-only throughout the guided flow.
 */
export function usePartnerLockedFundingSources() {
  const client = useSelector(selectClient);
  const { flags } = useFeatureFlags();
  const featureEnabled = flags.partnerFundingSplit === true;

  const hasPartnerClientId = featureEnabled && !!client?.partnerClientId;

  const { data: partnerClient = null, isLoading: isLoadingPartnerClient } = useQuery({
    queryKey: clientKeys.detail(client?.partnerClientId ?? ''),
    queryFn: async () => {
      const response = (await clientsApi.getById(
        client!.partnerClientId!,
      )) as unknown as ClientContainer;
      return response.client ?? null;
    },
    enabled: hasPartnerClientId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: partnerSessions, isLoading: isLoadingPartnerSessions } = useQuery({
    queryKey: guidedPlanningSessionKeys.list({ clientId: partnerClient?.clientId ?? '' }),
    queryFn: () => guidedPlanningApi.getSessions(partnerClient!.clientId),
    enabled: !!partnerClient?.clientId,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = hasPartnerClientId && (isLoadingPartnerClient || isLoadingPartnerSessions);

  const partnerActiveSession = partnerSessions?.[0] ?? null;

  const {
    partnerSharedFundingSourceIds,
    partnerSharedActionByFundingSourceId,
    partnerSharedOriginalValueByFundingSourceId,
    partnerSharedCustomPercentByFundingSourceId,
  } = useMemo(() => {
    const selections = partnerActiveSession?.stepData?.partnerAllocationSelections ?? {};
    const entries = Object.values(selections as Record<string, PersistedSelection>);
    const ids = new Set(entries.map(s => s.partnerFundingSourceId).filter((id): id is string => !!id));
    const actionMap: Record<string, string> = {};
    const originalValueMap: Record<string, number> = {};
    const customPercentMap: Record<string, number | undefined> = {};
    for (const s of entries) {
      if (s.partnerFundingSourceId) {
        actionMap[s.partnerFundingSourceId] = s.action;
        originalValueMap[s.partnerFundingSourceId] = s.originalValue;
        customPercentMap[s.partnerFundingSourceId] = s.customClientPercent;
      }
    }
    return {
      partnerSharedFundingSourceIds: ids,
      partnerSharedActionByFundingSourceId: actionMap,
      partnerSharedOriginalValueByFundingSourceId: originalValueMap,
      partnerSharedCustomPercentByFundingSourceId: customPercentMap,
    };
  }, [partnerActiveSession?.stepData?.partnerAllocationSelections]);

  return {
    isLoading,
    partnerClient,
    partnerActiveSession,
    partnerSharedFundingSourceIds,
    partnerSharedActionByFundingSourceId,
    partnerSharedOriginalValueByFundingSourceId,
    partnerSharedCustomPercentByFundingSourceId,
    partnerFirstName: partnerClient?.clientFirstName ?? null,
  };
}
